import { useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";
import { Audio as ExpoAudio } from "expo-av";
import * as FileSystem from "expo-file-system";
import { Buffer } from "buffer";

export type StreamAudioChunk = {
  seq: number;
  audioBase64: string;
  text?: string;
  isFinalChunk?: boolean;
};

/** Must match `LEARNING_TTS_SAMPLE_RATE_HZ` in backend Google Cloud TTS streaming config */
const PCM_SAMPLE_RATE = 24000;
const PCM_CHUNK_BLEND_SAMPLES = 32;
const PCM_CHANNELS = 1;
const PCM_BITS_PER_SAMPLE = 16;
const WAV_HEADER_SIZE_BYTES = 44;
const MIN_PLAYBACK_RATE = 1e-6;

class StreamingPcmPlayer {
  private readonly ctx: AudioContext;
  private readonly sampleRate: number;
  private nextPlayTime: number;
  private readonly activeSources = new Set<AudioBufferSourceNode>();
  private lastPcmSample: number | null = null;
  private playbackRate = 1;
  private readonly meterGain: GainNode;
  private readonly analyser: AnalyserNode;
  private readonly analyserData: Uint8Array<ArrayBuffer>;
  private meterAnimationFrameId: number | null = null;
  private onAudioLevel: ((level: number) => void) | null = null;
  private onAudioLevels: ((levels: number[]) => void) | null = null;

  constructor(sampleRate = PCM_SAMPLE_RATE) {
    this.sampleRate = sampleRate;
    this.ctx = new AudioContext({ sampleRate });
    this.nextPlayTime = this.ctx.currentTime + 0.05;
    this.meterGain = this.ctx.createGain();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.8;
    this.analyserData = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount));
    this.meterGain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
    this.startMeter();
  }

  async resume() {
    if (this.ctx.state !== "running") {
      await this.ctx.resume();
    }
  }

  enqueuePcm16(base64: string) {
    const decoded = this.decodePcm16(base64);
    if (decoded.length === 0) {
      return;
    }

    const pcm = new Float32Array(decoded);
    const blendLen = Math.min(PCM_CHUNK_BLEND_SAMPLES, pcm.length);
    if (this.lastPcmSample !== null && blendLen > 0) {
      const prev = this.lastPcmSample;
      for (let i = 0; i < blendLen; i += 1) {
        const t = (i + 1) / blendLen;
        pcm[i] = prev * (1 - t) + pcm[i] * t;
        pcm[i] = Math.max(-1, Math.min(1, pcm[i]));
      }
    }
    this.lastPcmSample = pcm[pcm.length - 1] ?? null;

    const buffer = this.ctx.createBuffer(1, pcm.length, this.sampleRate);
    buffer.copyToChannel(pcm, 0);

    const source = this.ctx.createBufferSource();
    const rate = Math.max(this.playbackRate, MIN_PLAYBACK_RATE);
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(this.meterGain);
    source.onended = () => {
      this.activeSources.delete(source);
    };
    this.activeSources.add(source);

    const now = this.ctx.currentTime;
    this.nextPlayTime = Math.max(this.nextPlayTime, now + 0.03);
    const startAt = this.nextPlayTime;
    source.start(startAt);
    this.nextPlayTime = startAt + buffer.duration / rate;
  }

  setPlaybackRate(rate: number) {
    const safeRate = Math.max(rate, MIN_PLAYBACK_RATE);
    this.playbackRate = safeRate;
    this.activeSources.forEach((source) => {
      source.playbackRate.value = safeRate;
    });
  }

  setOnAudioLevel(handler?: (level: number) => void) {
    this.onAudioLevel = handler ?? null;
    if (!this.onAudioLevel) {
      return;
    }
    this.onAudioLevel(0);
  }

  setOnAudioLevels(handler?: (levels: number[]) => void) {
    this.onAudioLevels = handler ?? null;
    if (!this.onAudioLevels) {
      return;
    }
    this.onAudioLevels([]);
  }

  reset() {
    for (const source of this.activeSources) {
      source.stop();
    }
    this.activeSources.clear();
    this.lastPcmSample = null;
    this.nextPlayTime = this.ctx.currentTime + 0.05;
    this.onAudioLevel?.(0);
    this.onAudioLevels?.([]);
  }

  suspend() {
    void this.ctx.suspend();
    this.onAudioLevel?.(0);
    this.onAudioLevels?.([]);
  }

  resumeContext() {
    void this.ctx.resume();
  }

  getRemainingPlaybackSeconds() {
    return Math.max(0, this.nextPlayTime - this.ctx.currentTime);
  }

  async destroy() {
    this.reset();
    this.stopMeter();
    await this.ctx.close();
  }

  private startMeter() {
    const tick = () => {
      this.analyser.getByteFrequencyData(this.analyserData);
      let sum = 0;
      for (let i = 0; i < this.analyserData.length; i += 1) {
        sum += this.analyserData[i];
      }
      const avg = this.analyserData.length > 0 ? sum / this.analyserData.length : 0;
      const level = Math.max(0, Math.min(1, avg / 128));
      this.onAudioLevel?.(level);

      if (this.onAudioLevels) {
        const barCount = 16;
        const samplesPerBar = Math.max(1, Math.floor(this.analyserData.length / barCount));
        const levels: number[] = [];
        for (let i = 0; i < barCount; i += 1) {
          const startIndex = i * samplesPerBar;
          const endIndex = Math.min(this.analyserData.length, startIndex + samplesPerBar);
          let binSum = 0;
          let count = 0;
          for (let j = startIndex; j < endIndex; j += 1) {
            binSum += this.analyserData[j];
            count += 1;
          }
          const average = count > 0 ? binSum / count : 0;
          levels.push(Math.max(0, Math.min(1, average / 128)));
        }
        this.onAudioLevels(levels);
      }
      this.meterAnimationFrameId = requestAnimationFrame(tick);
    };
    this.meterAnimationFrameId = requestAnimationFrame(tick);
  }

  private stopMeter() {
    if (this.meterAnimationFrameId != null) {
      cancelAnimationFrame(this.meterAnimationFrameId);
      this.meterAnimationFrameId = null;
    }
  }

  private decodePcm16(base64: string) {
    const bytes = Buffer.from(base64, "base64");
    const pcm = new Float32Array(Math.floor(bytes.length / 2));

    for (let i = 0; i < pcm.length; i += 1) {
      const lo = bytes[i * 2];
      const hi = bytes[i * 2 + 1];
      let sample = (hi << 8) | lo;
      if (sample >= 0x8000) {
        sample -= 0x10000;
      }
      pcm[i] = Math.max(-1, Math.min(1, sample / 32768));
    }

    return pcm;
  }
}

const pcmToWavBase64 = (
  pcmBase64: string,
  sampleRate = PCM_SAMPLE_RATE,
  channels = PCM_CHANNELS,
  bitsPerSample = PCM_BITS_PER_SAMPLE
) => {
  const pcmBuffer = Buffer.from(pcmBase64, "base64");
  const dataSize = pcmBuffer.length;
  const byteRate = sampleRate * channels * (bitsPerSample / 8);
  const blockAlign = channels * (bitsPerSample / 8);
  const wavBuffer = Buffer.alloc(WAV_HEADER_SIZE_BYTES + dataSize);

  wavBuffer.write("RIFF", 0, "ascii");
  wavBuffer.writeUInt32LE(36 + dataSize, 4);
  wavBuffer.write("WAVE", 8, "ascii");
  wavBuffer.write("fmt ", 12, "ascii");
  wavBuffer.writeUInt32LE(16, 16);
  wavBuffer.writeUInt16LE(1, 20);
  wavBuffer.writeUInt16LE(channels, 22);
  wavBuffer.writeUInt32LE(sampleRate, 24);
  wavBuffer.writeUInt32LE(byteRate, 28);
  wavBuffer.writeUInt16LE(blockAlign, 32);
  wavBuffer.writeUInt16LE(bitsPerSample, 34);
  wavBuffer.write("data", 36, "ascii");
  wavBuffer.writeUInt32LE(dataSize, 40);
  pcmBuffer.copy(wavBuffer, WAV_HEADER_SIZE_BYTES);
  return wavBuffer.toString("base64");
};

export function useAITutorAudioPlayback(autoStreamAudioEnabled: boolean, resourceId: string, playbackRate = 1) {
  const pcmPlayerRef = useRef<StreamingPcmPlayer | null>(null);
  const lastWebSeqRef = useRef(0);
  const onStreamAudioLevelRef = useRef<((level: number) => void) | undefined>(undefined);
  const onStreamAudioLevelsRef = useRef<((levels: number[]) => void) | undefined>(undefined);

  const webAudioUserPausedRef = useRef(false);
  const autoStreamAudioEnabledRef = useRef(autoStreamAudioEnabled);
  const onAutoStreamPlaybackEndedRef = useRef<(() => void) | undefined>(undefined);
  const streamAudioCompletedRef = useRef(false);
  const streamAudioEndNotifiedRef = useRef(false);
  const streamAudioDrainCheckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackRateRef = useRef(playbackRate);

  // Native fallback state.
  const nativeQueueRef = useRef<StreamAudioChunk[]>([]);
  const nativePlayingRef = useRef(false);
  const nativePlayedSeqRef = useRef(0);
  const soundObjectRef = useRef<ExpoAudio.Sound>(new ExpoAudio.Sound());

  const stopDrainCheckTimer = useCallback(() => {
    if (streamAudioDrainCheckTimerRef.current) {
      clearInterval(streamAudioDrainCheckTimerRef.current);
      streamAudioDrainCheckTimerRef.current = null;
    }
  }, []);

  const maybeNotifyAutoStreamPlaybackEnded = useCallback(() => {
    if (!streamAudioCompletedRef.current || streamAudioEndNotifiedRef.current) {
      return;
    }

    if (Platform.OS === "web") {
      const remainingSeconds = pcmPlayerRef.current?.getRemainingPlaybackSeconds() ?? 0;
      if (webAudioUserPausedRef.current || remainingSeconds > 0.08) {
        return;
      }
    } else {
      const hasPendingNativeChunk = nativeQueueRef.current.some((chunk) => chunk.seq > nativePlayedSeqRef.current);
      if (nativePlayingRef.current || hasPendingNativeChunk) {
        return;
      }
    }

    streamAudioEndNotifiedRef.current = true;
    stopDrainCheckTimer();
    onAutoStreamPlaybackEndedRef.current?.();
  }, [stopDrainCheckTimer]);

  const ensureDrainCheckTimer = useCallback(() => {
    if (streamAudioDrainCheckTimerRef.current) {
      return;
    }
    streamAudioDrainCheckTimerRef.current = setInterval(() => {
      maybeNotifyAutoStreamPlaybackEnded();
    }, 250);
  }, [maybeNotifyAutoStreamPlaybackEnded]);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return undefined;
    }
    pcmPlayerRef.current = new StreamingPcmPlayer(PCM_SAMPLE_RATE);
    pcmPlayerRef.current.setOnAudioLevel((level) => {
      onStreamAudioLevelRef.current?.(level);
    });
    pcmPlayerRef.current.setOnAudioLevels((levels) => {
      onStreamAudioLevelsRef.current?.(levels);
    });
    return () => {
      const player = pcmPlayerRef.current;
      pcmPlayerRef.current = null;
      if (player) {
        void player.destroy();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      stopDrainCheckTimer();
    };
  }, [stopDrainCheckTimer]);

  const resetAudioPlayback = useCallback(async () => {
    stopDrainCheckTimer();
    streamAudioCompletedRef.current = false;
    streamAudioEndNotifiedRef.current = false;
    if (Platform.OS === "web") {
      webAudioUserPausedRef.current = false;
      pcmPlayerRef.current?.reset();
      lastWebSeqRef.current = 0;
      return;
    }

    await soundObjectRef.current.stopAsync().catch(() => undefined);
    await soundObjectRef.current.unloadAsync().catch(() => undefined);
    nativeQueueRef.current = [];
    nativePlayedSeqRef.current = 0;
    nativePlayingRef.current = false;
  }, [stopDrainCheckTimer]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    const handlePageHidden = () => {
      void resetAudioPlayback();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void resetAudioPlayback();
      }
    };
    window.addEventListener("pagehide", handlePageHidden);
    window.addEventListener("beforeunload", handlePageHidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHidden);
      window.removeEventListener("beforeunload", handlePageHidden);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resetAudioPlayback]);

  useEffect(() => {
    void resetAudioPlayback();
  }, [resourceId, resetAudioPlayback]);

  useEffect(() => {
    autoStreamAudioEnabledRef.current = autoStreamAudioEnabled;
  }, [autoStreamAudioEnabled]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (Platform.OS === "web") {
      pcmPlayerRef.current?.setPlaybackRate(playbackRate);
      return;
    }
    void soundObjectRef.current.setRateAsync(playbackRate, true).catch(() => undefined);
  }, [playbackRate]);

  const playNextNativeChunk = useCallback(async () => {
    if (nativePlayingRef.current || Platform.OS === "web") {
      return;
    }

    const nextIndex = nativeQueueRef.current.findIndex((chunk) => chunk.seq > nativePlayedSeqRef.current);
    if (nextIndex === -1) {
      return;
    }
    const [chunk] = nativeQueueRef.current.splice(nextIndex, 1);
    if (!chunk?.audioBase64?.trim()) {
      return;
    }

    nativePlayingRef.current = true;
    try {
      const wavBase64 = pcmToWavBase64(chunk.audioBase64);
      const uri = `${FileSystem.documentDirectory}aitutor-stream-${chunk.seq}.wav`;
      await FileSystem.writeAsStringAsync(uri, wavBase64, { encoding: FileSystem.EncodingType.Base64 });
      await soundObjectRef.current.unloadAsync().catch(() => undefined);
      await soundObjectRef.current.loadAsync({ uri });
      await soundObjectRef.current.setRateAsync(playbackRateRef.current, true).catch(() => undefined);
      await soundObjectRef.current.playAsync();
      await new Promise<void>((resolve) => {
        soundObjectRef.current.setOnPlaybackStatusUpdate((status) => {
          if (status.isLoaded && status.didJustFinish) {
            resolve();
          }
        });
      });
      nativePlayedSeqRef.current = Math.max(nativePlayedSeqRef.current, chunk.seq);
    } finally {
      nativePlayingRef.current = false;
      void playNextNativeChunk();
      maybeNotifyAutoStreamPlaybackEnded();
    }
  }, [maybeNotifyAutoStreamPlaybackEnded]);

  const enqueueWebOrNativeChunk = useCallback(
    (payload: StreamAudioChunk) => {
      if (!payload.audioBase64?.trim()) {
        return;
      }

      if (Platform.OS === "web") {
        if (payload.seq <= lastWebSeqRef.current) {
          return;
        }
        lastWebSeqRef.current = payload.seq;
        void (async () => {
          if (!webAudioUserPausedRef.current) {
            await pcmPlayerRef.current?.resume();
          }
          pcmPlayerRef.current?.enqueuePcm16(payload.audioBase64);
          maybeNotifyAutoStreamPlaybackEnded();
        })();
        return;
      }

      const exists = nativeQueueRef.current.some((chunk) => chunk.seq === payload.seq);
      if (exists) {
        return;
      }
      nativeQueueRef.current.push(payload);
      nativeQueueRef.current.sort((a, b) => a.seq - b.seq);
      void playNextNativeChunk();
      maybeNotifyAutoStreamPlaybackEnded();
    },
    [maybeNotifyAutoStreamPlaybackEnded, playNextNativeChunk]
  );

  const handleStreamAutoAudioChunk = useCallback(
    (payload: StreamAudioChunk) => {
      if (!autoStreamAudioEnabledRef.current) {
        return;
      }
      enqueueWebOrNativeChunk(payload);
    },
    [enqueueWebOrNativeChunk]
  );

  const enqueueOnDemandAudioChunk = useCallback(
    (payload: StreamAudioChunk) => {
      enqueueWebOrNativeChunk(payload);
    },
    [enqueueWebOrNativeChunk]
  );

  const notifyStreamStarted = useCallback(() => {
    stopDrainCheckTimer();
    streamAudioCompletedRef.current = false;
    streamAudioEndNotifiedRef.current = false;
    if (Platform.OS !== "web") {
      return;
    }
    if (!webAudioUserPausedRef.current) {
      void pcmPlayerRef.current?.resume();
    }
  }, [stopDrainCheckTimer]);

  const notifyStreamCompleted = useCallback(() => {
    streamAudioCompletedRef.current = true;
    ensureDrainCheckTimer();
    maybeNotifyAutoStreamPlaybackEnded();
  }, [ensureDrainCheckTimer, maybeNotifyAutoStreamPlaybackEnded]);

  const suspendAudioPlayback = useCallback(() => {
    if (Platform.OS === "web") {
      webAudioUserPausedRef.current = true;
      pcmPlayerRef.current?.suspend();
    }
  }, []);

  const resumeAudioPlayback = useCallback(() => {
    if (Platform.OS === "web") {
      webAudioUserPausedRef.current = false;
      void pcmPlayerRef.current?.resumeContext();
    }
    maybeNotifyAutoStreamPlaybackEnded();
  }, [maybeNotifyAutoStreamPlaybackEnded]);

  const setOnAutoStreamPlaybackEnded = useCallback((handler?: () => void) => {
    onAutoStreamPlaybackEndedRef.current = handler;
  }, []);

  const setOnStreamAudioLevel = useCallback((handler?: (level: number) => void) => {
    onStreamAudioLevelRef.current = handler;
    if (Platform.OS === "web") {
      pcmPlayerRef.current?.setOnAudioLevel(handler);
    }
    handler?.(0);
  }, []);

  const setOnStreamAudioLevels = useCallback((handler?: (levels: number[]) => void) => {
    onStreamAudioLevelsRef.current = handler;
    if (Platform.OS === "web") {
      pcmPlayerRef.current?.setOnAudioLevels(handler);
    }
    handler?.([]);
  }, []);

  return {
    resetAudioPlayback,
    handleStreamAutoAudioChunk,
    enqueueOnDemandAudioChunk,
    notifyStreamStarted,
    notifyStreamCompleted,
    suspendAudioPlayback,
    resumeAudioPlayback,
    setOnAutoStreamPlaybackEnded,
    setOnStreamAudioLevel,
    setOnStreamAudioLevels,
  };
}
